// The processing cockpit — watch the agent think. One screen, three
// coordinated panes over a single task's event trace (orgs/{org}/tasks/{id},
// written by backend/shared/taskProgress.js):
//   left    trace — status, clickable step spans, event counters, live log
//   center  graph canvas (knowledge graph lighting up) + retrieval map
//   right   document heat — every section the run touched, with verdicts
//
// Time model: the scrubber runs over event SEQUENCE, not wall clock — flush
// batching gives bursts near-identical timestamps, but sequence cannot
// collide, so , / . step through a burst one verdict at a time. The task's
// `activeSpan` is live state ("model redrafting §c12–c19"), bound to real
// worker heartbeats — nothing in the canvases is interpolated or invented.
// A finished run replays identically to how it streamed: the persisted event
// array IS the audit record.
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useOrg } from '../data/orgs';
import { useDoc } from '../data/firestore';
import TaskProgress from '../components/TaskProgress';
import GraphCanvas from '../components/cockpit/GraphCanvas';
import RetrievalMap from '../components/cockpit/RetrievalMap';
import '../components/cockpit/cockpit.css';

const KIND_LABEL = {
  derive: 'AI refactor',
  ingest: 'Document ingest',
  graph: 'Knowledge graph build',
  index: 'Project brain indexing',
};

const fmtClock = (at) => new Date(at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
const fmtDur = (ms) => {
  if (ms == null) return '—';
  const s = Math.max(0, Math.round(ms / 1000));
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
};
const STEP_ICON = { done: '✓', active: '›', pending: '○', failed: '✕' };

export default function Cockpit() {
  const { taskId } = useParams();
  const { orgId } = useOrg();
  const { doc: task } = useDoc(orgId && taskId ? `orgs/${orgId}/tasks/${taskId}` : null);

  // Base layer for the graph canvas. A derive's own doc has no graph until
  // the run finishes — the SOURCE document's graph is the map; the new doc
  // is the redline. taskProgress stamps sourceDealId/sourceDocId at claim.
  const baseDealId = task?.sourceDocId ? task.sourceDealId : task?.dealId;
  const baseDocId = task?.sourceDocId || task?.docId;
  const graphPath = baseDealId && baseDocId
    ? `orgs/${orgId}/deals/${baseDealId}/docs/${baseDocId}/graph/current`
    : null;
  const { doc: graphDoc } = useDoc(graphPath);
  const baseGraph = graphDoc?.terms ? graphDoc : null; // spilled-to-S3 graphs skip the base layer

  const running = task?.status === 'running';
  const events = useMemo(() => task?.events || [], [task?.events]);

  // Scrubbing = a cursor into the event sequence. null = live edge.
  const [cursor, setCursor] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [stepSpan, setStepSpan] = useState(null); // { id, from, to } — step time filter
  const atLive = cursor == null;
  const visibleCount = atLive ? events.length : Math.min(cursor, events.length);

  useEffect(() => {
    if (!playing) return undefined;
    const step = Math.max(1, Math.round(events.length / 240)); // full replay ≈ 20s
    const iv = setInterval(() => {
      setCursor((c) => {
        const next = (c ?? 0) + step;
        if (next >= events.length) { setPlaying(false); return null; }
        return next;
      });
    }, 80);
    return () => clearInterval(iv);
  }, [playing, events.length]);

  // , / . step the cursor one event at a time — through bursts, not over them.
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key !== ',' && e.key !== '.') return;
      setPlaying(false);
      setCursor((c) => {
        const cur = c ?? events.length;
        const next = e.key === ',' ? Math.max(0, cur - 1) : cur + 1;
        return next >= events.length ? null : next;
      });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [events.length]);

  const visible = useMemo(() => {
    const slice = events.slice(0, visibleCount);
    return stepSpan ? slice.filter((e) => e.at >= stepSpan.from && e.at <= stepSpan.to) : slice;
  }, [events, visibleCount, stepSpan]);

  // The live span only binds to the canvases at the live edge — during
  // playback the model isn't "working on" anything.
  const activeSpan = atLive && running ? task?.activeSpan : null;
  const workingIds = useMemo(() => activeSpan?.sectionIds || [], [activeSpan]);

  if (!orgId) return <div className="cs-empty">Select an organization first.</div>;
  if (task === undefined) return <div className="cs-skeleton" style={{ height: 200 }} />;
  if (task === null) return <div className="cs-empty">Task not found — it may belong to another organization.</div>;

  const counters = Object.entries(task.eventCounts || {}).sort((a, b) => b[1] - a[1]);
  const docLink = task.docId ? `/deals/${task.dealId}/docs/${task.docId}` : task.dealId ? `/deals/${task.dealId}` : null;
  const lastVisible = visibleCount > 0 ? events[visibleCount - 1] : null;
  const mode = task.mode || task.kind;
  // Burst context at the scrub position: "plan.section 12/44" beats a bare
  // event number when 44 verdicts share one timestamp.
  let burst = null;
  if (!atLive && lastVisible) {
    let from = visibleCount - 1;
    let to = visibleCount - 1;
    while (from > 0 && events[from - 1].type === lastVisible.type) from -= 1;
    while (to < events.length - 1 && events[to + 1].type === lastVisible.type) to += 1;
    burst = { type: lastVisible.type, nth: visibleCount - 1 - from + 1, of: to - from + 1 };
  }
  const questions = visible.filter((e) => e.type === 'question.asked');

  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-4)' }}>
      <div className="u-split">
        <div className="u-stack" style={{ '--gap': '2px' }}>
          <h1 style={{ margin: 0, fontSize: 'var(--fs-xl)' }}>{task.title || task.kind}</h1>
          <span className="u-small u-muted">
            {KIND_LABEL[task.kind] || task.kind} · started {fmtClock(task.startedAt)} ·{' '}
            {running ? 'running' : `${task.status} in ${fmtDur(task.runMs ?? (task.finishedAt ? task.finishedAt - task.startedAt : null))}`}
            {task.costUsd > 0 && ` · $${task.costUsd.toFixed(2)}`}
          </span>
        </div>
        <span className="u-cluster" style={{ '--gap': 'var(--sp-2)', flexShrink: 0 }}>
          {docLink && <Link className="cs-btn cs-btn-outline cs-btn-sm" to={docLink}>Open document</Link>}
          <Link className="cs-btn cs-btn-ghost cs-btn-sm" to="/activity">All activity</Link>
        </span>
      </div>

      {/* Playback — a cursor over the event sequence. , / . step one event. */}
      <div className="cockpit-playbar">
        <button
          type="button" className="cs-btn cs-btn-outline cs-btn-sm"
          onClick={() => { setStepSpan(null); if (playing) { setPlaying(false); } else { setCursor(0); setPlaying(true); } }}
        >
          {playing ? 'Pause' : 'Replay'}
        </button>
        <input
          type="range" min={0} max={events.length} step={1}
          value={visibleCount}
          onChange={(e) => { setPlaying(false); setStepSpan(null); const v = Number(e.target.value); setCursor(v >= events.length ? null : v); }}
        />
        <span className="u-xs u-tabular" style={{ flexShrink: 0 }}>
          {atLive ? (
            <span className={running ? 'is-live' : 'u-muted'}>
              {running ? '● LIVE' : 'end of run'} · {events.length.toLocaleString()} events
            </span>
          ) : (
            <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => { setPlaying(false); setCursor(null); }}>
              event {visibleCount}/{events.length}
              {burst && burst.of > 1 ? ` · ${burst.type} ${burst.nth}/${burst.of}` : lastVisible ? ` · ${lastVisible.type}` : ''}
              {' — jump to '}{running ? 'live' : 'end'}
            </button>
          )}
        </span>
        <span className="u-xs u-muted" style={{ flexShrink: 0 }} title="Step one event back / forward">, / .</span>
      </div>

      {/* Scrubbed-event detail: what this event is and what caused it. */}
      {!atLive && lastVisible && (
        <div className="cockpit-eventdetail u-xs u-tabular">
          seq {lastVisible.seq} · {lastVisible.type}
          {lastVisible.id ? ` · ${lastVisible.id}` : ''}
          {lastVisible.heading ? ` · ${String(lastVisible.heading).slice(0, 60)}` : ''}
          {lastVisible.action ? ` · ${lastVisible.action}` : ''}
          {Array.isArray(lastVisible.causedBy) && lastVisible.causedBy.length > 0 && ` · caused by seq ${lastVisible.causedBy.join(', ')}`}
        </div>
      )}

      {/* Wall-clock spine: where each step sat in the run. The event stream is
          bursty (one flush per model call); the steps are the honest clock. */}
      {(task.steps || []).some((s) => s.startedAt) && <StepGantt task={task} />}

      {/* What the model is chewing on right now — real worker state, not animation. */}
      {activeSpan && (
        <div className="cockpit-activespan">
          <span className="cockpit-activespan-dot" />
          <span className="cockpit-activespan-label">{activeSpan.label}</span>
          <SpanElapsed startedAt={activeSpan.startedAt} />
        </div>
      )}

      <div className="cockpit-grid">
        {/* Left: the trace */}
        <div className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
          {(task.steps || []).length > 0 && (
            <div className="cockpit-panel">
              <div className="cockpit-panel-head"><strong>Steps</strong><span>click to isolate</span></div>
              <div className="u-stack" style={{ '--gap': 0, padding: 'var(--sp-1)' }}>
                {task.steps.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className={`cockpit-step is-${s.status} ${stepSpan?.id === s.id ? 'is-selected' : ''}`}
                    onClick={() => {
                      if (stepSpan?.id === s.id) { setStepSpan(null); return; }
                      if (s.startedAt) setStepSpan({ id: s.id, from: s.startedAt, to: s.startedAt + (s.ms ?? (Date.now() - s.startedAt)) });
                    }}
                  >
                    <span className="cockpit-step-icon">{STEP_ICON[s.status] || '○'}</span>
                    <span className="cockpit-step-label">{s.label}</span>
                    <span className="cockpit-step-ms u-tabular">{s.ms != null ? fmtDur(s.ms) : ''}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {counters.length > 0 && (
            <div className="cockpit-counters">
              {counters.map(([type, n]) => (
                <span key={type} className="cockpit-counter">{type} × {n.toLocaleString()}</span>
              ))}
            </div>
          )}
          <TaskProgress task={task} progress={task} hideSteps />
        </div>

        {/* Center: the canvases */}
        <div className="u-stack" style={{ '--gap': 'var(--sp-3)', minWidth: 0 }}>
          <div className="cockpit-panel">
            <div className="cockpit-panel-head">
              <strong>Knowledge graph</strong>
              <span>
                {task.sourceDocId ? 'dim base = source document · ' : ''}
                amber = adapt, red = remove, green = edited, dashed = parse debris{workingIds.length ? ' · cyan = in the model now' : ''}
              </span>
            </div>
            <GraphCanvas baseGraph={baseGraph} events={visible} workingIds={workingIds} height={400} />
          </div>
          <div className="cockpit-panel">
            <div className="cockpit-panel-head">
              <strong>Vector retrieval</strong>
              <span>star = query · orbit distance = similarity</span>
            </div>
            <RetrievalMap events={visible} mode={mode} eventCounts={task.eventCounts} height={300} />
          </div>
        </div>

        {/* Right: agent questions + document heat */}
        <div className="u-stack" style={{ '--gap': 'var(--sp-3)', minWidth: 0 }}>
          {questions.length > 0 && (
            <div className="cockpit-panel cockpit-questions">
              <div className="cockpit-panel-head">
                <strong>Agent questions</strong>
                <span>{questions.some((q) => q.blocks) ? 'blocking' : 'run proceeded with the source value hedged'}</span>
              </div>
              <div className="u-stack" style={{ '--gap': 'var(--sp-1)', padding: 'var(--sp-2)' }}>
                {questions.map((q) => (
                  <div key={q.seq} className="cockpit-question u-small">{q.question}</div>
                ))}
              </div>
            </div>
          )}
          <div className="cockpit-panel">
            <div className="cockpit-panel-head"><strong>Document</strong><span>sections this run touched</span></div>
            <DocHeat events={visible} docLink={docLink} workingIds={workingIds} />
            {mode === 'derive' && !running && (
              <div className="u-xs u-muted" style={{ padding: 'var(--sp-2)', borderTop: '1px solid var(--border)' }}>
                The derived draft's own knowledge graph is built by a separate indexing task after this run — this pane shows the run's trace, not the destination graph.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Compact horizontal Gantt of the step checklist against wall clock. Live
// steps extend to now; the bar is the truthful "where did the time go".
function StepGantt({ task }) {
  const t0 = task.startedAt;
  const t1 = task.finishedAt || Date.now();
  const total = Math.max(1, t1 - t0);
  const steps = (task.steps || []).filter((s) => s.startedAt);
  return (
    <div className="cockpit-gantt">
      {steps.map((s) => {
        const from = ((s.startedAt - t0) / total) * 100;
        const ms = s.ms ?? (s.status === 'active' ? Date.now() - s.startedAt : 0);
        const w = Math.max(0.5, (ms / total) * 100);
        return (
          <div
            key={s.id}
            className={`cockpit-gantt-bar is-${s.status}`}
            style={{ left: `${from}%`, width: `${Math.min(w, 100 - from)}%` }}
            title={`${s.label} — ${fmtDur(ms)}`}
          >
            <span>{s.id}</span>
          </div>
        );
      })}
    </div>
  );
}

function SpanElapsed({ startedAt }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  return <span className="u-xs u-tabular u-muted">{fmtDur(Date.now() - startedAt)}</span>;
}

// Every section the run has seen so far, with its latest verdict — the
// "so what" pane: click through to the clause in the real document.
function DocHeat({ events, docLink, workingIds }) {
  const sections = useMemo(() => {
    const map = new Map();
    for (const e of events) {
      if (e.type === 'parse.section' || e.type === 'section.loaded') {
        map.set(e.id, { id: e.id, heading: e.heading || '', state: 'parsed', fragment: e.quality === 'fragment' });
      } else if (e.type === 'plan.section') {
        const cur = map.get(e.id) || { id: e.id, heading: '' };
        map.set(e.id, { ...cur, heading: cur.heading || e.heading || '', state: e.action || 'keep', rationale: e.rationale });
      } else if (e.type === 'redraft.started') {
        const cur = map.get(e.id) || { id: e.id, heading: '' };
        map.set(e.id, { ...cur, heading: cur.heading || e.heading || '', state: 'redrafting' });
      } else if (e.type === 'edit.proposed') {
        const cur = map.get(e.id) || { id: e.id, heading: '' };
        map.set(e.id, { ...cur, heading: cur.heading || e.heading || '', state: e.op === 'add' ? 'add' : 'proposed' });
      } else if (e.type === 'edit.applied') {
        const cur = map.get(e.id) || { id: e.id, heading: '' };
        map.set(e.id, {
          ...cur,
          heading: cur.heading || e.heading || '',
          state: e.op === 'add' ? 'add' : cur.state === 'remove' ? 'remove' : 'edited',
        });
      }
    }
    return [...map.values()];
  }, [events]);

  if (!sections.length) {
    return <div className="cs-empty" style={{ border: 0 }}>Sections appear as the run parses and plans them.</div>;
  }
  const working = new Set(workingIds || []);
  const stateClass = { adapt: 'is-adapt', remove: 'is-remove', add: 'is-add', proposed: 'is-proposed', edited: 'is-edited', keep: 'is-keep', redrafting: 'is-redrafting' };
  return (
    <div className="cockpit-doc">
      {sections.map((s) => {
        const cls = `cockpit-doc-row ${stateClass[s.state] || ''} ${s.fragment ? 'is-fragment' : ''} ${working.has(s.id) ? 'is-working' : ''}`;
        const inner = (
          <>
            <span className="cockpit-doc-id">{s.id}</span>
            <span className="cockpit-doc-heading" title={s.rationale || s.heading}>{s.heading || s.rationale || '—'}</span>
            {s.fragment && <span className="u-xs cockpit-doc-fragment" style={{ flexShrink: 0 }}>parse fragment</span>}
            {working.has(s.id)
              ? <span className="u-xs cockpit-doc-state-working" style={{ flexShrink: 0 }}>in model</span>
              : s.state !== 'parsed' && <span className="u-xs" style={{ flexShrink: 0, opacity: 0.75 }}>{s.state === 'redrafting' ? 'in model' : s.state}</span>}
          </>
        );
        return docLink ? (
          <Link key={s.id} className={cls} to={`${docLink}?clause=${s.id}`}>{inner}</Link>
        ) : (
          <span key={s.id} className={cls}>{inner}</span>
        );
      })}
    </div>
  );
}
